import React, { useState, useEffect } from 'react';

function LocationCapture({ onLocationFound, className = '' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [status, setStatus] = useState('loading');
  const [locationName, setLocationName] = useState('');
  const [locationSource, setLocationSource] = useState('none');
  const [debugInfo, setDebugInfo] = useState({});
  const [showDebug, setShowDebug] = useState(false);
  const [needsManualLocation, setNeedsManualLocation] = useState(false);

  const DEFAULT_LOCATION = {
    lat: -37.8136,
    lon: 144.9631,
    address: 'Melbourne, VIC',
    source: 'default'
  };

  const isInAustralia = (lat, lon) => {
    return lat >= -45 && lat <= -10 && lon >= 110 && lon <= 155;
  };

  useEffect(() => {
    const getLocation = async () => {
      const debug = {
        timestamp: new Date().toISOString(),
        gps: null,
        ipServices: [],
        chosen: null
      };
      
      try {
        // 1. Check localStorage FIRST - this is the most reliable source
        const savedLat = localStorage.getItem('op_user_lat');
        const savedLng = localStorage.getItem('op_user_lng');
        const savedName = localStorage.getItem('op_user_location');
        
        debug.saved = { lat: savedLat, lng: savedLng, name: savedName };
        
        if (savedLat && savedLng) {
          const lat = parseFloat(savedLat);
          const lng = parseFloat(savedLng);
          if ((lat !== 0 || lng !== 0) && isInAustralia(lat, lng)) {
            debug.chosen = { source: 'saved', lat, lng };
            setDebugInfo(debug);
            onLocationFound({
              lat: lat,
              lon: lng,
              address: savedName || 'Your saved location',
              source: 'saved'
            });
            setLocationName(savedName || 'Your saved location');
            setLocationSource('saved');
            setStatus('idle');
            return;
          } else if (lat !== 0 || lng !== 0) {
            debug.saved.rejected = 'Not in Australia';
          }
        }

        // 2. Try browser geolocation but validate it
        if (navigator.geolocation) {
          try {
            const pos = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 0
              });
            });
            const { latitude, longitude, accuracy } = pos.coords;
            debug.gps = { lat: latitude, lon: longitude, accuracy };
            
            // Only accept GPS location if it's in Australia AND accurate
            const inAU = isInAustralia(latitude, longitude);
            const isAccurate = accuracy < 100; // Less than 100m accuracy
            
            if (inAU && isAccurate) {
              try {
                const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`);
                if (geoRes.ok) {
                  const geoData = await geoRes.json();
                  debug.gps.address = geoData.display_name;
                  const address = geoData.display_name || 'Your location';
                  debug.chosen = { source: 'gps', lat: latitude, lng: longitude };
                  setDebugInfo(debug);
                  onLocationFound({ lat: latitude, lon: longitude, address, source: 'gps' });
                  setLocationName(address);
                  setLocationSource('gps');
                  localStorage.setItem('op_user_lat', latitude);
                  localStorage.setItem('op_user_lng', longitude);
                  localStorage.setItem('op_user_location', address);
                  setStatus('idle');
                  return;
                }
              } catch (e) {
                debug.gps.addressError = e.message;
              }
            } else {
              debug.gps.rejected = !inAU ? 'Not in Australia' : 'Accuracy too low';
              debug.gps.inAustralia = inAU;
              debug.gps.isAccurate = isAccurate;
            }
          } catch (e) {
            debug.gps = { error: e.message || e.code || 'Unknown error' };
          }
        } else {
          debug.gps = { error: 'Geolocation not supported' };
        }

        // 3. Try IP-based location but validate it
        const ipServices = [
          { name: 'ipapi.co', url: 'https://ipapi.co/json/', latKey: 'latitude', lonKey: 'longitude', cityKey: 'city', regionKey: 'region', countryKey: 'country_name' },
          { name: 'ip-api.com', url: 'https://ip-api.com/json/', latKey: 'lat', lonKey: 'lon', cityKey: 'city', regionKey: 'regionName', countryKey: 'country' }
        ];
        
        for (const service of ipServices) {
          try {
            const res = await fetch(service.url);
            if (res.ok) {
              const data = await res.json();
              const lat = data[service.latKey];
              const lon = data[service.lonKey];
              const city = data[service.cityKey] || '';
              const region = data[service.regionKey] || '';
              const country = data[service.countryKey] || '';
              
              const inAU = lat && lon ? isInAustralia(lat, lon) : false;
              
              debug.ipServices.push({
                name: service.name,
                lat, lon, city, region, country,
                inAustralia: inAU
              });
              
              // Only accept IP location if it's in Australia
              if (lat && lon && inAU) {
                const address = `${city ? city + ', ' : ''}${region ? region + ', ' : ''}${country}`.trim() || 'Australia';
                debug.chosen = { source: 'ip', service: service.name, lat, lon, address };
                setDebugInfo(debug);
                onLocationFound({ lat, lon, address, source: 'ip' });
                setLocationName(address);
                setLocationSource('ip');
                localStorage.setItem('op_user_lat', lat);
                localStorage.setItem('op_user_lng', lon);
                localStorage.setItem('op_user_location', address);
                setStatus('idle');
                return;
              }
            }
          } catch (e) {
            debug.ipServices.push({
              name: service.name,
              error: e.message
            });
          }
        }

        // 4. Could not determine location in Australia - ask user to search
        debug.chosen = { source: 'needs-manual', reason: 'No reliable AU location found' };
        setDebugInfo(debug);
        setNeedsManualLocation(true);
        setLocationName('');
        setLocationSource('none');
        onLocationFound(DEFAULT_LOCATION);
        setStatus('idle');
        
      } catch (error) {
        debug.error = error.message;
        setDebugInfo(debug);
        setNeedsManualLocation(true);
        onLocationFound(DEFAULT_LOCATION);
        setStatus('idle');
      }
    };

    getLocation();
  }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) return;
    
    setIsSearching(true);
    setStatus('searching');
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=5&addressdetails=1&countrycodes=AU`
      );
      if (res.ok) {
        const data = await res.json();
        const formatted = data.map(item => ({
          lat: item.lat,
          lon: item.lon,
          display_name: item.display_name,
          type: item.type || 'location'
        }));
        setResults(formatted);
        setShowResults(formatted.length > 0);
      }
    } catch (error) {
      console.error('Location search failed:', error);
    } finally {
      setIsSearching(false);
      setStatus('idle');
    }
  };

  const selectLocation = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    const address = result.display_name || 'Selected location';
    onLocationFound({
      lat: lat,
      lon: lon,
      address: address,
      source: 'search'
    });
    setLocationName(address);
    setLocationSource('search');
    setNeedsManualLocation(false);
    setShowResults(false);
    setSearchTerm(address);
    localStorage.setItem('op_user_lat', lat);
    localStorage.setItem('op_user_lng', lon);
    localStorage.setItem('op_user_location', address);
  };

  const switchToGps = () => {
    if (!navigator.geolocation) {
      alert('GPS not available on this device');
      return;
    }
    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const inAU = isInAustralia(latitude, longitude);
        
        setDebugInfo(prev => ({
          ...prev,
          gps: { lat: latitude, lon: longitude, accuracy, inAustralia: inAU }
        }));
        
        if (inAU) {
          onLocationFound({
            lat: latitude,
            lon: longitude,
            address: 'Your current location',
            source: 'gps'
          });
          setLocationName('Your current location');
          setLocationSource('gps');
          setNeedsManualLocation(false);
          localStorage.setItem('op_user_lat', latitude);
          localStorage.setItem('op_user_lng', longitude);
          localStorage.setItem('op_user_location', 'Your current location');
        } else {
          alert(`Your GPS location is in the United States (${latitude.toFixed(4)}, ${longitude.toFixed(4)}).\n\nThis is likely because your internet connection (Starlink) is routing through a US ground station.\n\nPlease use the search box above to enter your suburb, city, or postcode.`);
          setNeedsManualLocation(true);
        }
        setStatus('idle');
      },
      (error) => {
        setDebugInfo(prev => ({
          ...prev,
          gps: { error: error.message, code: error.code }
        }));
        alert(`Could not get GPS location: ${error.message}\n\nPlease use the search box above to enter your suburb, city, or postcode.`);
        setNeedsManualLocation(true);
        setStatus('idle');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (status === 'loading') {
    return (
      <div className={`flex items-center gap-2 text-sm text-gray-500 ${className}`}>
        <span className="animate-pulse">📍</span>
        <span>Finding your location...</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {needsManualLocation && (
        <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          ⚠️ We couldn't detect your location automatically (your internet provider may route through another country). Please enter your suburb, city, or postcode below.
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span 
          className="text-sm text-gray-700 whitespace-nowrap font-medium"
          title={`Location source: ${locationSource}`}
        >
          📍 {locationName || 'Set location'}
        </span>
        <div className="flex flex-1 min-w-[180px] gap-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search suburb, city, or postcode..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[120px]"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !searchTerm.trim()}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-sm"
          >
            {isSearching ? '⏳' : '🔍'}
          </button>
          <button
            onClick={switchToGps}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
            title="Try GPS again"
          >
            📍 GPS
          </button>
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm"
            title="Show debug info"
          >
            🐛
          </button>
        </div>
      </div>
      
      <p className="text-xs text-gray-400 mt-1">
        {locationSource === 'gps' && '📍 Using your current GPS location'}
        {locationSource === 'ip' && '📍 Approximate location based on your IP address'}
        {locationSource === 'saved' && '📍 Your saved location'}
        {locationSource === 'search' && '🔍 Manually selected location'}
        {locationSource === 'default' && '📍 Default location (Melbourne) - search above to change'}
      </p>

      {showDebug && (
        <div className="mt-2 p-3 bg-gray-900 text-green-400 text-xs font-mono rounded-lg overflow-auto max-h-60">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-bold">🐛 Location Debug</span>
            <button onClick={() => setShowDebug(false)} className="text-white">✕</button>
          </div>
          <pre className="whitespace-pre-wrap">{JSON.stringify(debugInfo, null, 2)}</pre>
        </div>
      )}

      {showResults && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((result, idx) => (
            <div
              key={idx}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 text-sm"
              onClick={() => selectLocation(result)}
            >
              <div className="font-medium">{result.display_name}</div>
              <div className="text-xs text-gray-500">{result.type || 'Location'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LocationCapture;
